/**
 * guestInvite.ts
 *
 * Shared helpers for turning the raw guest token list (emails and @usernames)
 * collected on the private-event form into the deduplicated, validated
 * `guest_emails` / `guest_usernames` payload fields sent to the API.
 */

export interface GuestEntry {
  email: string;
}

export interface GuestPayloadFields {
  guest_emails?: string[];
  guest_usernames?: string[];
}

export function isValidGuestEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** True if any non-username guest token is not a validly formatted email. */
export function hasInvalidGuestEmail(guests: GuestEntry[] | undefined): boolean {
  if (!guests?.length) return false;
  return guests.some((g) => {
    const trimmed = g.email.trim();
    return !trimmed.startsWith('@') && !isValidGuestEmail(trimmed);
  });
}

/**
 * Splits guest tokens into deduplicated (case-insensitive) email and
 * username arrays. Invalid email tokens are dropped defensively — callers
 * should already block submission via `hasInvalidGuestEmail` before this
 * ever needs to filter anything out.
 */
export function buildGuestPayloadFields(guests: GuestEntry[] | undefined): GuestPayloadFields {
  if (!guests?.length) return {};

  const emails: string[] = [];
  const seenEmails = new Set<string>();
  const usernames: string[] = [];
  const seenUsernames = new Set<string>();

  for (const { email: token } of guests) {
    const trimmed = token.trim();
    if (!trimmed) continue;

    if (trimmed.startsWith('@')) {
      const username = trimmed.slice(1);
      const key = username.toLowerCase();
      if (username && !seenUsernames.has(key)) {
        seenUsernames.add(key);
        usernames.push(username);
      }
      continue;
    }

    if (!isValidGuestEmail(trimmed)) continue;
    const key = trimmed.toLowerCase();
    if (!seenEmails.has(key)) {
      seenEmails.add(key);
      emails.push(trimmed);
    }
  }

  const result: GuestPayloadFields = {};
  if (emails.length) result.guest_emails = emails;
  if (usernames.length) result.guest_usernames = usernames;
  return result;
}
